import { css } from '@emotion/react';
import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { values } from 'underscore';
import moment from 'moment';

import t from 'react-translate';
import { AngularServicesContext } from 'react-app';

import { AdminUser } from 'redux/schemas/models/help-options';
import { getCurrentUser } from 'redux/selectors/users';

import { textXSFontSize } from 'styles/global_defaults/fonts';
import { halfSpacing, quarterSpacing, standardSpacing, threeQuartersSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import { gray6, gray7 } from 'styles/global_defaults/colors';

import NvDatePicker, { DatePickerType, machineDateAndTimezoneDateConversion } from 'shared/components/inputs/nv-datepicker';
import SelectAdmins from './select-admins';

type DailyReportConfigurationModalProps = {
  showModal: Dispatch<SetStateAction<boolean>>;
  onSubmit: (data: DailyReportConfig) => Promise<boolean>;
  initialConfig?: {
    scheduledAt: string;
    recipientUserIds: number[];
  };
};

export type DailyReportConfig = {
  scheduledAt: string;
  recipientUserIds: AdminUser[];
};

const styles = css`
  .button-bar {
    margin-top: ${tripleSpacing}px;
  }
  .tag-item {
    height: ${standardSpacing}px !important;
    border-radius: ${threeQuartersSpacing}px;
    padding: 3px ${halfSpacing}px;
  }
  .icon {
    font-size: ${textXSFontSize}px;
  }
  .bs4-form-control {
    border-radius: ${quarterSpacing}px;
  }
  .loading {
    background: linear-gradient(90deg, ${gray6} 35%, ${gray7} 45%, ${gray6} 55%);
    background-size: 600% 600%;
    animation: shineAnimation 1.5s infinite;
  }

  .placeholder-bar {
    width: 100%;
    height: ${quarterSpacing}px;
    margin-bottom: ${standardSpacing}px;
    margin-left: auto;
    margin-right: auto;
  }
  .short-bar {
    margin-left: 0;
    margin-right: 0;
    width: 50%;
  }
`;

const DailyReportConfigurationModal: React.FC<DailyReportConfigurationModalProps> = ({
  showModal,
  onSubmit,
  initialConfig,
}) => {
  const { $state } = useContext(AngularServicesContext);
  const { catalogId } = $state?.params;

  const datepickerRef = useRef(null);

  const courseUsers = useSelector((state) => state.models.courseUsers[catalogId]);
  const currentUser = useSelector((state) => getCurrentUser(state));
  const { scheduledAt: initialScheduledAt, recipientUserIds } = initialConfig;

  const convertedTime = initialScheduledAt
    ? machineDateAndTimezoneDateConversion(
      moment(initialScheduledAt).toDate(),
      currentUser?.timeZone,
      false,
    ).toString()
    : '';

  const getMappedAdmins = useCallback(() => recipientUserIds
    .map((id) => values(courseUsers)
      .find((user) => user.user.id === id))
    .map((user) => ({
      id: user.user.id,
      user: user?.user as AdminUser['user'],
    }))
    .filter(Boolean) as AdminUser[],
  [courseUsers, recipientUserIds]);

  const methods = useForm<DailyReportConfig>({
    defaultValues: {
      scheduledAt: convertedTime,
      recipientUserIds: recipientUserIds?.length > 0 && courseUsers ? getMappedAdmins() : [],
    },
    resolver: yupResolver(yup.object().shape({
      scheduledAt: yup.date()
        .transform((value, originalValue) => {
          if (originalValue?._isAMomentObject) {
            return originalValue.toDate();
          }
          return value;
        })
        .typeError(t.VALIDATION.TIME())
        .required(t.VALIDATION.REQUIRED()),
      recipientUserIds: yup.array().of(
        yup.object().shape({
          id: yup.number(),
          user: yup.object().shape({
            firstName: yup.string(),
            lastName: yup.string(),
          }),
        }),
      ).required(t.VALIDATION.REQUIRED()),
    })),
    mode: 'onChange',
  });

  const { control, handleSubmit, reset, formState } = methods;
  const { isDirty, isValid, isSubmitting } = formState;

  useEffect(() => {
    if (courseUsers && recipientUserIds?.length > 0) {
      methods.reset({
        scheduledAt: convertedTime,
        recipientUserIds: getMappedAdmins(),
      });
    }
  }, [
    courseUsers, recipientUserIds, initialScheduledAt,
    currentUser.timeZone, methods, convertedTime, getMappedAdmins,
  ]);

  const onConfigSubmit = async (data: DailyReportConfig) => {
    const success = await onSubmit(data);
    if (success) {
      showModal(false);
      reset();
    }
  };

  return (
    <div className='d-flex flex-column py-4 px-7' css={styles}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onConfigSubmit)}>
          <div>{t.CUSTOM_DOWNLOADS.DAILY_REPORT.MODAL.DESCRIPTION()}</div>
          <div className='mb-1 text-small gray-3 font-weight-bold mt-4'>
            {t.CUSTOM_DOWNLOADS.DAILY_REPORT.MODAL.TIME()}
            <span className='text-danger ml-1'>*</span>
          </div>
          <NvDatePicker
            ref={datepickerRef}
            type={DatePickerType.TIME}
            placement='bottom-start'
            withForm
            name='scheduledAt'
            min={moment()}
            fixed
          />
          <SelectAdmins control={control} />
          <div className='button-bar'>
            <Button
              variant='secondary'
              type='button'
              onClick={() => {
                showModal(false);
                reset();
              }}
              disabled={isSubmitting}
            >
              {t.FORM.CANCEL()}
            </Button>
            <Button
              variant='primary'
              className='ml-2'
              type='submit'
              disabled={!isDirty || !isValid || isSubmitting}
            >
              {isSubmitting ? t.FORM.SAVING() : t.FORM.SAVE()}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default DailyReportConfigurationModal;
